<template>
  <BasePage title="Folder">

  <div class="columns">
    <Loader v-if="loading" class="ml-4"/>
    <div v-else class="column is-10-desktop is-offset-1-desktop">

      <div class="columns flex-column-reverse-is-mobile">
        <div class="column is-narrow max-width-300px-if-not-mobile">
          <aside class="menu">
            <FolderSection />
            <GroupsSection />
          </aside>
        </div>
        <div class="column" >
          <div class="d-flex align-items-center mb-2">
            <nav class="breadcrumb is-large">
              <ul>
                <li><router-link to="/" class="pl-0">Home</router-link></li>
                <li class="is-active">
                  <span v-if="!editing" class="ml-2 d-flex flex-wrap align-items-center">
                    <FolderIcon width="28px" height="35px" class="mr-2"/>
                    <span class="mr-2">{{ folder.title }}</span>
                    <SharedWithUsersIcon
                      v-if="folder.shared"
                      width="1.5rem"
                      height="1.5rem"
                    />
                    <span class="my-tag ml-2 p-4">{{ videoCount }}</span>
                  </span>
                  <span v-else class="ml-2 level-item justify-content-start align-items-center">
                    <input @keyup.enter="save" class="input" type="text" v-model="editedName">
                    <div class="buttons d-flex ml-1">
                      <button class="button" @click="editing = false">Cancel</button>
                      <button class="button is-primary" :class="loading && 'is-loading'" @click="save">Save</button>
                    </div>
                  </span>
                </li>
                <a v-if="!editing" class="button is-link" @click="editing = true">edit</a>
              </ul>
            </nav>
          </div>

          <div class="d-flex align-items-center mb-4 flex-wrap-reverse-if-mobile">
            <div class="d-flex align-items-center w-100">
              <input class="input mt-0 br-0 mr-2"
              type="search"
              v-model="query"
              placeholder="search in folder">
              <a @click="rows = true" class="d-flex align-items-center if-not-mobile">
                <BarsIcon fill="#dbdbdb" width="25px"/>
              </a>
              <a @click="rows = false" class="pl-2 d-flex align-items-center mr-2 if-not-mobile">
                <TableIcon fill="#dbdbdb" width="25px"/>
              </a>
            </div>
            <button
              v-if="!loading && userID == folder.owner"
              @click="showShareModal = true"
              type='button'
              class='button is-info w-100-if-mobile mb-4-if-mobile mr-1'
              name='share folder'>
              Share Folder
            </button>
            <ShareFolderModal
              :folder ="folder"
              v-if="showShareModal && !loading"
              @close="showShareModal = false"
            />
            <button
              v-if="!loading"
              @click="showConfirm = true"
              type='button'
              class='button is-danger w-100-if-mobile mb-4-if-mobile mr-1'
              name='share folder'>
              {{ label }}
            </button>


                <Confirm
                  :show="showConfirm"
                  :message.once="confirmMessage"
                  :title.once="label"
                  :confirmFocus.once="true"
                  :completeButtonText.once="'Remove'"
                  @confirm="leaveFolder(folder.id)"
                  @cancel="showConfirm = false"
                />
          </div>

          <section class="d-flex justify-content-center w-100 align-items-center" v-if="loading">
            <Loader />
          </section>

          <section>
            <VideosTable
            :class="rows ? 'rows' : 'grid'"
            :videos="videosResults"
            />

            <div v-if="nothingFound && !emptyFolder" class="d-flex align-items-center flex-column" >
              <p class="text-muted text-center fs-2rem break-word">
              No videos found matching: <strong>{{ query }}</strong>
              </p>
            </div>

            <div v-if="emptyFolder">
              <p class="text-muted text-center fs-2rem">This folder is empty</p>
              <p class="text-muted text-center fs-1rem">Add a video with the <b>Move to Folder</b> button on the video's page.</p>
            </div>

          </section>
        </div>
      </div>
    </div>
  </div>

  </BasePage>
</template>

<script>
import store from '../store'
import axios from 'axios'

import BasePage from './BasePage.vue'
import Confirm from './Confirm.vue'
import VideoCard from './VideoCard.vue'
import VideosTable from './VideosTable.vue'
import Loader from './Loader.vue'
import GroupsSection from './GroupsSection.vue'
import TableIcon from './TableIcon.vue'
import BarsIcon from './BarsIcon.vue'
import FolderIcon from './FolderIcon.vue'
import FolderSection from './FolderSection.vue'
import ShareFolderModal from './ShareFolderModal.vue'
import SharedWithUsersIcon from './SharedWithUsersIcon.vue'

import { matchesQuery } from '../search'
import { byCreatedOn } from '../sorters'

import {
  leaveFolder,
  deleteFolder,
} from '../actionTypes'

export default {
  name: 'Folder',
  components: {
    BasePage,
    Confirm,
    VideoCard,
    Loader,
    VideosTable,
    GroupsSection,
    BarsIcon,
    TableIcon,
    FolderIcon,
    FolderSection,
    ShareFolderModal,
    SharedWithUsersIcon,
  },
  async mounted () {
    await store.dispatch('fetchFolders')
    this.loading = false
  },
  props: {
    id: {
      type: Number,
    },
  },
  data () {
    return {
      query: '',
      showMyVideos: true,
      rows: true,
      loading: true,
      showConfirm: false,
      confirmMessage: 'Are you sure?',
      showShareModal: false,
      editing: false,
      editedName: '',
    }
  },
  computed: {
    folder () {
      return store.state.folders.find(x => x.id === this.id)
    },
    videos () {
      return this.folder.videos
    },
    videosResults () {
      return this.videos.sort(byCreatedOn).filter(x => matchesQuery(x, this.query))
    },
    nothingFound () {
      return this.videosResults.length === 0 && this.query !== ''
    },
    emptyFolder () {
      return this.videos.length === 0
    },
    videoCount () {
      const count = this.videos.length
      return count === 1
        ? `${count} video`
        : `${count} videos`
    },
    userID () {
      return store.state.user.id
    },
    label () {
      return this.userID === this.folder.owner
        ? 'Delete Folder'
        : 'Leave Folder'
    }
  },
  watch: {
    editing () {
      this.editedName = this.folder.title
    }
  },
  methods: {
    resendConfirmationEmail () {
      store.dispatch('resendEmailConfirmation')
    },
    shareFolder (folderID) {
      console.log(folderID)
    },
    async leaveFolder (folderID) {
      if (this.userID === this.folder.owner) {
        await store.dispatch(deleteFolder, folderID)
      } else {
        await store.dispatch(leaveFolder, folderID)
      }
      this.showConfirm = false
      this.$router.push('/')
    },
    save () {
      axios.patch(`/api/v1/folders/${this.id}/`, { title: this.editedName },
        { headers: { 'Authorization': 'Token ' + localStorage.getItem('token') } })
      .then(async () => {
        await store.dispatch('fetchFolders')
        this.editing = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
  .video-section {
    max-height: 800px;
    overflow-y:scroll;
  }

  .add-video {
    height: 10rem;
    display: flex;
    /* flex-grow: 1; */
    width: 30rem;
    color: #23c6c8;
    font-size: 2rem;
    margin-bottom: 1rem;
    margin-top: 0rem;

    border: 2px dashed #23c6c8;
    border-radius: 5px;
    background: white;

    @media screen and (max-width: 500px) {
      font-size: 2rem;
    }
  }

  .tab-border-bottom {
    border-bottom: 1px solid #dbdbdb;
  }
  .grid {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  .flex-column-reverse-is-mobile {
    @media (max-width: 769px) {
      display: flex;
      flex-direction: column-reverse;
    }
  }
</style>



// WEBPACK FOOTER //
// Folder.vue?c7a6acca